import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLocale } from '../contexts/LocaleContext';
import { Sun, Moon, Monitor } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SidebarMenuButton } from './ui/sidebar';
import { ButtonTooltip, TooltipContent } from './ui/tooltip';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  collapsed?: boolean;
  className?: string;
  iconClassName?: string;
  tooltipSide?: React.ComponentProps<typeof TooltipContent>['side'];
  menuSide?: React.ComponentProps<typeof DropdownMenuContent>['side'];
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ collapsed, className, iconClassName, tooltipSide = 'right', menuSide }) => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t } = useLocale();

  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;

  const trigger = (
    <SidebarMenuButton
      className={cn('group/theme-toggle h-[38px] py-0', collapsed && '!justify-center !text-center !gap-0', className)}
      data-sidebar="menu-button"
    >
      <ThemeIcon
        className={cn(
          collapsed ? '!h-6 !w-6 text-primary' : '!h-4 !w-4 text-secondary-foreground',
          'transition-[transform,color] duration-300 group-hover/theme-toggle:rotate-45 group-hover/theme-toggle:text-[var(--scribane-green)]',
          iconClassName,
        )}
      />
      {!collapsed && (
        <span className="text-primary transition-colors group-hover/theme-toggle:text-[var(--scribane-green)]">
          {t('theme')}
        </span>
      )}
    </SidebarMenuButton>
  );

  return (
    <DropdownMenu>
      <ButtonTooltip tooltip={t('theme')} side={tooltipSide}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      </ButtonTooltip>
      <DropdownMenuContent
        align="start"
        side={menuSide ?? (collapsed ? 'right' : 'bottom')}
        collisionPadding={8}
      >
        <DropdownMenuItem onClick={() => setTheme('light')}>
          <Sun className="h-4 w-4" />
          {t('theme_light')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('dark')}>
          <Moon className="h-4 w-4" />
          {t('theme_dark')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('system')}>
          <Monitor className="h-4 w-4" />
          {t('theme_system')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
